import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { FileText, ArrowRight, Loader2, Send, Download, Clock, FolderOpen, AlertCircle, Gift, Copy, Check, Users, DollarSign, TrendingUp, Mail, MessageSquare, Share2, Linkedin, Twitter, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/forms/Field";
import { FileDropzone } from "@/components/forms/FileDropzone";
import { statusColor, FINANCING_TYPES, DOC_CATEGORIES } from "@/data/site";
import { LenderMatch } from "@/components/site/LenderMatch";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABEL = { loan: "Loan Application", emd: "EMD Funding", transactional: "Transactional Funding" };

const money = (v) => {
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  if (!v || isNaN(n) || n === 0) return null;
  return "$" + n.toLocaleString("en-US");
};

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${statusColor(status)}`} data-testid="status-badge">{status}</span>;
}

function ProductName(data) {
  const t = data?.financing_type;
  return FINANCING_TYPES.find((x) => x.value === t)?.label;
}

export function PortalDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/portal/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loading />;

  return (
    <div data-testid="portal-dashboard">
      <h1 className="h-display text-3xl text-white">Welcome back{d.user.name ? `, ${d.user.name.split(" ")[0]}` : ""}.</h1>
      <p className="mt-1 text-dim">Here's the latest on your financing.</p>

      <div className="mt-6 grid grid-cols-3 gap-4">
        {[["Total", d.counts.total], ["Active", d.counts.active], ["Docs Needed", d.counts.documents_needed]].map(([k, v]) => (
          <div key={k} className="card-surface rounded-xl p-5">
            <div className="eyebrow">{k}</div>
            <div className="font-mono text-3xl text-white mt-1">{v}</div>
          </div>
        ))}
      </div>

      <h2 className="h-display text-xl text-white mt-10 mb-4">Your Submissions</h2>
      {d.submissions.length === 0 ? (
        <Empty onClick={() => navigate("/apply")} />
      ) : (
        <div className="space-y-3">
          {d.submissions.map((s) => (
            <button key={s.id} onClick={() => navigate(`/portal/applications/${s.id}`)} data-testid={`portal-sub-${s.reference}`}
              className="w-full text-left card-surface rounded-xl p-5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-gold text-sm">{s.reference}</span>
                  <span className="text-xs text-faint">· {ProductName(s.data) || TYPE_LABEL[s.type]}</span>
                </div>
                <div className="text-white font-semibold truncate mt-1">{s.property_label}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <StatusBadge status={s.status} />
                <ArrowRight className="w-4 h-4 text-faint" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalApplications() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  useEffect(() => { api.get("/portal/applications").then((r) => setSubs(r.data)).catch(() => setSubs([])); }, []);
  if (!subs) return <Loading />;
  return (
    <div data-testid="portal-applications">
      <div className="flex items-center justify-between">
        <h1 className="h-display text-3xl text-white">Applications</h1>
        <Button onClick={() => navigate("/apply")} className="btn-gold rounded-full h-11 px-5 gap-2" data-testid="portal-new-application">New Deal <ArrowRight className="w-4 h-4" /></Button>
      </div>
      {subs.length === 0 ? <Empty onClick={() => navigate("/apply")} /> : (
        <div className="mt-6 space-y-3">
          {subs.map((s) => (
            <button key={s.id} onClick={() => navigate(`/portal/applications/${s.id}`)} className="w-full text-left card-surface rounded-xl p-5 flex items-center justify-between">
              <div>
                <span className="font-mono text-gold text-sm">{s.reference}</span>
                <div className="text-white font-semibold mt-1">{s.property_label}</div>
                <div className="text-xs text-faint mt-0.5">{ProductName(s.data) || TYPE_LABEL[s.type]}</div>
              </div>
              <StatusBadge status={s.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DealSnapshot({ s }) {
  const d = s.data || {};
  const rows = [
    ["Property Address", s.property_address],
    ["State", s.property_state],
    ["Loan Type", ProductName(d) || TYPE_LABEL[s.type]],
    ["Property Type", d.property_type],
    ["Purchase Price", money(d.purchase_price)],
    ["Current Value", money(d.current_value)],
    ["After Repair Value", money(d.arv)],
    ["Rehab Budget", money(d.rehab_budget)],
    ["Closing Date", d.closing_date],
  ].filter(([, v]) => v && String(v).trim());
  if (rows.length === 0) return null;
  return (
    <div className="mt-6 card-surface rounded-xl p-5" data-testid="portal-deal-snapshot">
      <div className="eyebrow mb-3">Deal Summary</div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-4 border-b border-edge/50 pb-2">
            <dt className="text-sm text-dim shrink-0">{k}</dt>
            <dd className="text-sm text-white text-right font-medium break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function PortalApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const [uploadDocs, setUploadDocs] = useState([]);
  const [uploadCat, setUploadCat] = useState("Other");

  const load = () => api.get(`/portal/applications/${id}`).then((r) => setS(r.data)).catch(() => setS(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (s === null) return <Loading />;
  if (s === false) return <div className="text-dim">Application not found. <button className="text-gold" onClick={() => navigate("/portal")}>Back</button></div>;

  const send = async () => {
    if (!msg.trim()) return;
    try { const { data } = await api.post(`/portal/messages/${id}`, { body: msg }); setS((p) => ({ ...p, messages: [...(p.messages || []), data] })); setMsg(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const attach = async () => {
    if (!uploadDocs.length) return;
    try {
      for (const d of uploadDocs) {
        const fd = new FormData();
        fd.append("doc_id", d.id);
        await api.post(`/portal/applications/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Documents added"); setUploadDocs([]); load();
    } catch (e) { toast.error("Upload failed"); }
  };

  const requestLender = async (m) => {
    try {
      await api.post(`/portal/applications/${id}/request-lender`, { lender_id: m.lender_id, lender_name: m.lender_name });
      toast.success(`Requested ${m.lender_name}. Our team will reach out.`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Request failed"); }
  };

  const getPreapproval = async () => {
    try {
      await api.post(`/portal/applications/${id}/request-preapproval`);
      toast.success("Pre-approval issued — download your letter below."); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate pre-approval"); }
  };

  const submitToLender = async (m) => {
    if (!m?.lender_id) { toast.error("No lender selected"); return; }
    try {
      await api.post(`/portal/applications/${id}/submit-to-lender`, { lender_id: m.lender_id });
      toast.success(`BullsEye is submitting your deal to ${m.lender_name}. You'll get a confirmation by email.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submission failed"); }
  };

  const pendingDocs = (s.requested_documents || []).filter((r) => !r.fulfilled);

  return (
    <div data-testid="portal-application-detail">
      <button onClick={() => navigate("/portal/applications")} className="text-sm text-dim hover:text-gold mb-4">← Back to applications</button>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <span className="font-mono text-gold">{s.reference}</span>
          <h1 className="h-display text-2xl sm:text-3xl text-white mt-1">{s.property_label}</h1>
        </div>
        <StatusBadge status={s.status} />
      </div>

      <DealSnapshot s={s} />

      <LenderMatch data={s.lender_match} variant="borrower" subId={id}
        requestedLenderId={s.requested_lender?.id} onRequest={requestLender}
        termSheetBase={`${API}/portal/applications/${id}/term-sheet`} />

      <div className="mt-6 card-surface rounded-xl p-5" data-testid="portal-preapproval">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="eyebrow">Pre-Approval Letter</div>
            <p className="text-sm text-dim mt-1 max-w-md">
              {s.preapproval
                ? `Issued ${new Date(s.preapproval.issued_at).toLocaleDateString()} · valid through ${s.preapproval.valid_until}. Branded and signed by BullsEye Capital.`
                : "Get a branded BullsEye Capital pre-approval letter for this deal — great for making offers with confidence."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!s.preapproval && (
              <Button onClick={getPreapproval} className="btn-gold rounded-full h-11 px-5 gap-2" data-testid="get-preapproval-btn">
                <Award className="w-4 h-4" /> Get Pre-Approval
              </Button>
            )}
            {s.preapproval && (
              <a href={`${API}/portal/applications/${id}/preapproval-letter`} target="_blank" rel="noreferrer" data-testid="download-preapproval">
                <Button className="btn-gold rounded-full h-11 px-5 gap-2"><Download className="w-4 h-4" /> Download Letter</Button>
              </a>
            )}
          </div>
        </div>
      </div>

      {(() => {
        const best = s.requested_lender
          ? (s.lender_match?.matches || []).find((m) => m.lender_id === s.requested_lender.id) || { lender_id: s.requested_lender.id, lender_name: s.requested_lender.name }
          : s.lender_match?.best_match;
        const subs = s.lender_submissions || [];
        return (
          <div className="mt-6 card-surface rounded-xl p-5" data-testid="portal-submit-lender">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="eyebrow">One-Click Lender Submission</div>
                <p className="text-sm text-dim mt-1 max-w-md">
                  {best ? <>Have BullsEye package and submit your deal to <strong className="text-gold">{best.lender_name}</strong> — cover sheet, MISMO file, and all your documents, handled for you.</>
                        : "A recommended lender will appear here once your match is ready."}
                </p>
              </div>
              {best && (
                <Button onClick={() => submitToLender(best)} className="btn-gold rounded-full h-11 px-5 gap-2" data-testid="portal-submit-lender-btn">
                  <Send className="w-4 h-4" /> Submit with BullsEye
                </Button>
              )}
            </div>
            {subs.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="portal-submission-status">
                {subs.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t border-edge pt-2">
                    <span className="text-white">{r.lender_name} <span className="text-faint text-xs">· {r.method}</span></span>
                    <span className="text-xs text-gold">{r.status === "confirmed" ? `Confirmed${r.confirmation_number ? ` · #${r.confirmation_number}` : ""}` : r.status.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
        <div className="mt-6 rounded-xl border border-warn/40 bg-warn/10 p-5" data-testid="portal-docs-requested">
          <div className="flex items-center gap-2 text-warn font-semibold"><AlertCircle className="w-5 h-5" /> Documents Requested ({pendingDocs.length})</div>
          <ul className="mt-2 space-y-1 text-sm text-white/85">{pendingDocs.map((r, i) => <li key={i}>• {r.item}</li>)}</ul>
        </div>
      )}

      {/* status timeline */}
      <div className="mt-6 card-surface rounded-xl p-5">
        <div className="eyebrow mb-3">Status Timeline</div>
        <div className="space-y-3">
          {(s.status_history || []).map((h, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <Clock className="w-4 h-4 text-gold" />
              <span className="text-white font-medium">{h.status}</span>
              <span className="text-faint text-xs ml-auto">{new Date(h.at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* documents */}
      <div className="mt-6 card-surface rounded-xl p-5">
        <div className="eyebrow mb-3">Documents</div>
        {(s.documents || []).length > 0 ? (
          <ul className="space-y-2 mb-4">
            {s.documents.map((d, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg border border-edge bg-surface/50 px-3 py-2 text-sm">
                <span className="text-white/85 flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> {d.original_filename} <span className="text-faint text-xs">· {d.category}</span></span>
                <a href={`${API}/files/${d.id}/download`} target="_blank" rel="noreferrer" className="text-gold text-xs inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> View</a>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-faint mb-4">No documents yet.</p>}
        <div className="border-t border-edge pt-4">
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div className="sm:col-span-1">
              <label className="text-xs text-dim mb-1 block">Category</label>
              <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} className="w-full h-11 rounded-md bg-surface/70 border border-edge text-white text-sm px-3" data-testid="portal-upload-category">
                {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3"><FileDropzone category={uploadCat} value={uploadDocs} onChange={setUploadDocs} testId="portal-doc-upload" /></div>
          {uploadDocs.length > 0 && <Button onClick={attach} className="btn-gold rounded-full h-10 px-5 mt-3" data-testid="portal-attach-docs">Add to Application</Button>}
        </div>
      </div>

      {/* messages */}
      <div className="mt-6 card-surface rounded-xl p-5" data-testid="portal-messages-thread">
        <div className="eyebrow mb-3">Messages</div>
        <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
          {(s.messages || []).length === 0 && <p className="text-sm text-faint">No messages yet. Send a note to the team below.</p>}
          {(s.messages || []).map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-xl px-4 py-2.5 ${m.sender_role === "borrower" ? "ml-auto bg-gold/15 border border-gold/30" : "bg-surface border border-edge"}`}>
              <div className="text-xs text-faint mb-0.5">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</div>
              <div className="text-sm text-white/90">{m.body}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 h-11 rounded-full bg-surface/70 border border-edge text-white px-4 text-sm" data-testid="portal-message-input" />
          <Button onClick={send} className="btn-gold rounded-full h-11 w-11 p-0" data-testid="portal-message-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}

export function PortalDocuments() {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get("/portal/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])); }, []);
  if (!docs) return <Loading />;
  return (
    <div data-testid="portal-documents">
      <h1 className="h-display text-3xl text-white">Documents</h1>
      {docs.length === 0 ? <p className="mt-6 text-faint">No documents uploaded yet.</p> : (
        <div className="mt-6 space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="card-surface rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-white/85 text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> {d.original_filename} <span className="text-faint text-xs">· {d.category}</span></span>
              <a href={`${API}/files/${d.id}/download`} target="_blank" rel="noreferrer" className="text-gold text-xs inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> View</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalMessages() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  useEffect(() => { api.get("/portal/applications").then((r) => setSubs(r.data)).catch(() => setSubs([])); }, []);
  if (!subs) return <Loading />;
  return (
    <div data-testid="portal-messages">
      <h1 className="h-display text-3xl text-white">Messages</h1>
      <p className="mt-1 text-dim">Open an application to message the BullsEye Capital team.</p>
      <div className="mt-6 space-y-3">
        {subs.length === 0 && <p className="text-faint">No applications yet.</p>}
        {subs.map((s) => (
          <button key={s.id} onClick={() => navigate(`/portal/applications/${s.id}`)} className="w-full text-left card-surface rounded-xl p-4 flex items-center justify-between">
            <span><span className="font-mono text-gold text-sm">{s.reference}</span> <span className="text-white ml-2">{s.property_label}</span></span>
            <ArrowRight className="w-4 h-4 text-faint" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function PortalProfile() {
  const { user } = useOutletContext();
  const [f, setF] = useState({ name: user?.name || "", company: user?.company || "", phone: user?.phone || "" });
  const [busy, setBusy] = useState(false);
  const set = (n, v) => setF((s) => ({ ...s, [n]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/portal/profile", f); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="portal-profile">
      <h1 className="h-display text-3xl text-white">Profile</h1>
      <div className="mt-6 card-surface rounded-xl p-6 max-w-lg">
        <div className="text-sm text-faint mb-4">Email: <span className="text-white">{user?.email}</span></div>
        <div className="space-y-4">
          <TextField label="Full Name" name="name" value={f.name} onChange={set} testId="profile-name" />
          <TextField label="Company" name="company" value={f.company} onChange={set} testId="profile-company" />
          <TextField label="Phone" name="phone" value={f.phone} onChange={set} testId="profile-phone" />
        </div>
        <Button onClick={save} disabled={busy} className="btn-gold rounded-full h-11 px-6 mt-5" data-testid="profile-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}</Button>
      </div>
    </div>
  );
}

function Loading() { return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>; }
function Empty({ onClick }) {
  return (
    <div className="mt-6 card-surface rounded-2xl p-10 text-center">
      <FolderOpen className="w-10 h-10 text-gold mx-auto" />
      <p className="mt-3 text-dim">No submissions yet.</p>
      <Button onClick={onClick} className="btn-gold rounded-full h-11 px-6 mt-4 gap-2" data-testid="portal-empty-cta">Apply for Financing <ArrowRight className="w-4 h-4" /></Button>
    </div>
  );
}

const TIER_STYLE = {
  standard: "bg-white/10 text-white/80 border-white/20",
  mid: "bg-gold/15 text-gold border-gold/40",
  strategic: "bg-gold/25 text-goldhi border-gold/60",
};

export function PortalReferrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/portal/referrals").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const link = `${window.location.origin}/apply?ref=${data.code}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 1800); };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "BullsEye Capital", text: "Get your real estate deal funded with BullsEye Capital.", url: link }); } catch (e) { /* dismissed */ } }
    else copy();
  };
  const fmt = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="portal-referrals">
      <div className="flex items-center gap-2">
        <Gift className="w-5 h-5 text-gold" />
        <h1 className="font-display text-3xl text-white">Referral Program</h1>
      </div>
      <p className="mt-2 text-dim text-sm max-w-2xl">Refer a borrower or deal to BullsEye Capital. You get paid when the loan closes and funds — earning a share of our 1.75% fee based on your partner tier.</p>

      <div className="mt-6 card-surface rounded-xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="eyebrow">Your Partner Tier</div>
            <span className={`inline-block mt-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${TIER_STYLE[data.tier] || TIER_STYLE.standard}`} data-testid="referral-tier">
              {data.tier_label} · {Math.round(data.rate * 100)}% of fee · {data.range}
            </span>
          </div>
          <a href="/referral-program" target="_blank" rel="noreferrer" className="text-sm text-gold hover:text-goldhi">View program details →</a>
        </div>
        <div className="mt-4">
          <div className="eyebrow mb-1.5">Your Referral Link</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input readOnly value={link} data-testid="referral-link" className="flex-1 rounded-lg bg-surface/70 border border-edge text-white text-sm px-3 h-11" />
            <Button onClick={copy} className="btn-gold rounded-lg h-11 px-5 gap-2" data-testid="referral-copy-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="referral-share">
            <span className="text-xs text-faint mr-1">Share:</span>
            {(() => {
              const msg = `Get your real estate deal funded with BullsEye Capital. Apply here: ${link}`;
              const subj = "Get your deal funded with BullsEye Capital";
              const targets = [
                { key: "email", Icon: Mail, label: "Email", href: `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(msg)}` },
                { key: "sms", Icon: MessageSquare, label: "Text", href: `sms:?&body=${encodeURIComponent(msg)}` },
                { key: "whatsapp", Icon: Send, label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(msg)}` },
                { key: "x", Icon: Twitter, label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(subj)}&url=${encodeURIComponent(link)}` },
                { key: "linkedin", Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` },
              ];
              return targets.map((t) => (
                <a key={t.key} href={t.href} target="_blank" rel="noreferrer" title={`Share via ${t.label}`} data-testid={`referral-share-${t.key}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-edge hover:border-gold/60 hover:bg-gold/10 text-white/80 hover:text-gold px-3 h-9 text-sm transition-colors">
                  <t.Icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
                </a>
              ));
            })()}
            <button onClick={nativeShare} title="More share options" data-testid="referral-share-native"
              className="inline-flex items-center gap-1.5 rounded-full border border-edge hover:border-gold/60 hover:bg-gold/10 text-white/80 hover:text-gold px-3 h-9 text-sm transition-colors">
              <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">More</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Users, label: "Total Referrals", value: data.stats.total },
          { icon: Check, label: "Funded", value: data.stats.funded },
          { icon: DollarSign, label: "Earned (funded)", value: fmt(data.stats.earned_total) },
          { icon: TrendingUp, label: "Pending (est.)", value: fmt(data.stats.pending_total) },
        ].map((s) => (
          <div key={s.label} className="card-surface rounded-xl p-4">
            <s.icon className="w-4 h-4 text-gold" />
            <div className="mt-2 text-2xl font-display text-white">{s.value}</div>
            <div className="text-xs text-faint">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-semibold text-white mb-3">Your Referred Deals</h2>
        {data.referrals.length === 0 ? (
          <div className="card-surface rounded-xl p-8 text-center text-dim text-sm" data-testid="referral-empty">Share your link to start earning. Referred deals appear here.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-edge">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-faint bg-surface/60 border-b border-edge">
                  <th className="py-2.5 px-3 font-semibold">Reference</th>
                  <th className="py-2.5 px-3 font-semibold">Borrower</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                  <th className="py-2.5 px-3 font-semibold">Loan Amount</th>
                  <th className="py-2.5 px-3 font-semibold">Your Payout</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.map((r) => (
                  <tr key={r.reference} className="border-b border-edge/50" data-testid={`referral-row-${r.reference}`}>
                    <td className="py-2.5 px-3 text-white font-medium">{r.reference}</td>
                    <td className="py-2.5 px-3 text-white/75">{r.applicant}</td>
                    <td className="py-2.5 px-3"><StatusBadge status={r.status} /></td>
                    <td className="py-2.5 px-3 text-white/85">{r.amount ? fmt(r.amount) : "—"}</td>
                    <td className="py-2.5 px-3">
                      <span className={r.basis === "funded" ? "text-ok font-semibold" : "text-gold font-semibold"}>{fmt(r.payout)}</span>
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-faint">{r.basis === "funded" ? "earned" : "est."}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-faint">Estimated payouts are indicative and based on each deal's estimated loan amount. Referral compensation is paid on closed and funded transactions only. Final terms are subject to referral agreement and applicable law.</p>
      </div>
    </div>
  );
}
